import { ReactNode, useEffect, useRef, useState } from 'react';

interface AccordionItemData {
    content: ReactNode;
    accordionTabLayout: ReactNode;
}

interface AccordionProps {
    items: AccordionItemData[];
    getCurrentOpenIndexFromAccordion?: (currentIndex: number | null) => void;
}
interface AccordionItemProps {
    content: ReactNode;
    isOpen: boolean;
    onClick: () => void;
    accordionTabLayout: ReactNode;
    index: number;
}
const AccordionItem: React.FC<AccordionItemProps> = ({
    content,
    isOpen,
    onClick,
    accordionTabLayout,
    index,
}) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const itemRef = useRef<HTMLDivElement | null>(null);
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const [showContent, setShowContent] = useState(false);

    useEffect(() => {
        setShowContent(true);
        if (isOpen && contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight);
        } else {
            setContentHeight(0);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && itemRef.current) {
            const timeoutId = setTimeout(() => {
                itemRef?.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }, 600);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen]);

    return (
        <div className="relative">
            <div
                ref={itemRef}
                className={`w-full  ${
                    index === 0 && 'absolute -top-4'
                }  h-1 my-1.5`}
            ></div>
            <div className="w-full flex flex-col bg-white border border-gray-200 rounded-xl">
                <div
                    className="w-full py-4 px-3 flex items-center cursor-pointer min-h-14"
                    onClick={onClick}
                >
                    {accordionTabLayout}
                </div>

                <div
                    className="overflow-hidden transition-[height] duration-500 ease-in-out"
                    style={{ height: `${contentHeight}px` }}
                >
                    {showContent && (
                        <div ref={contentRef} className="px-4 pb-4">
                            <div className="relative overflow-hidden">
                                {content}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Accordion: React.FC<AccordionProps> = ({
    items,
    getCurrentOpenIndexFromAccordion,
}) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const handleClick = (index: number) => {
        setOpenIndex(prevIndex => (prevIndex === index ? null : index));
    };

    useEffect(() => {
        if (getCurrentOpenIndexFromAccordion) {
            getCurrentOpenIndexFromAccordion(openIndex);
        }
    }, [openIndex, getCurrentOpenIndexFromAccordion]);

    return (
        <div className="w-full flex flex-col pb-20 md:pb-2">
            {items.map((item, index) => {
                return (
                    <div key={index + 'accordionItem'}>
                        <div>
                            <AccordionItem
                                accordionTabLayout={item.accordionTabLayout}
                                content={item.content}
                                onClick={() => {
                                    handleClick(index);
                                }}
                                isOpen={index === openIndex}
                                index={index}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Accordion;
